import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { CommentContact } from './models';
import { baseApi } from '@centerpiece/components';

export const comment_contact = baseApi
  .enhanceEndpoints({ addTagTypes: ['CommentContacts', 'CommentContact'] })
  .injectEndpoints({
    endpoints: builder => ({
      getCommentContacts: builder.query<CommentContact[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: `comment_contact`,
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'CommentContact' as const,
                  id,
                })),
                'CommentContacts',
              ]
            : ['CommentContacts'],
      }),
      getCommentContact: builder.query<
        CommentContact,
        { id: number; params?: object }
      >({
        query: ({ id, params }: { id: number; params?: object }) => ({
          url: `comment_contact/${id}`,
          method: 'GET',
          params: params ? { rollup: 'all', ...params } : { rollup: 'all' },
        }),
        providesTags: (result, error, arg) => [
          { type: 'CommentContact', id: arg.id },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createCommentContact: builder.mutation({
        query: request => ({
          url: 'comment_contact',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['CommentContacts'],
      }),
      patchCommentContact: builder.mutation<CommentContact, CommentContact>({
        query: request => ({
          url: `comment_contact/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'CommentContact', id: arg.id },
        ],
      }),
      updateCommentContact: builder.mutation<CommentContact, CommentContact>({
        query: request => ({
          url: `comment_contact/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'CommentContact', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            comment_contact.util.updateQueryData(
              'getCommentContact',
              { id: id },
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(comment_contact.util.invalidateTags(['CommentContacts']))
             */
            dispatch(comment_contact.util.invalidateTags(['CommentContacts']));
          }
        },
      }),
      deleteCommentContact: builder.mutation<CommentContact, number>({
        query: id => ({
          url: `comment_contact/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'CommentContact' as const, arg }, 'CommentContacts']
            : ['CommentContacts'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  usePatchCommentContactMutation,
  useGetCommentContactsQuery,
  useGetCommentContactQuery,
  useCreateCommentContactMutation,
  useUpdateCommentContactMutation,
  useDeleteCommentContactMutation,
  usePrefetch: useCommentContactsPrefetch,
} = comment_contact;
const selectCommentContactsRoot =
  comment_contact.endpoints.getCommentContacts.select({});
export const selectCommentContacts = createSelector(
  selectCommentContactsRoot,
  comment_contact => comment_contact.data || []
);
